import React, { TextareaHTMLAttributes, forwardRef, useCallback, useEffect, useState } from 'react'
import cn from 'clsx'
import InputWrap from './InputWrap'
import styled from 'styled-components'
import { rem } from 'polished'
import { InputTextBase } from 'LuxKit/Typography/InputText'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'
import { mediaQueryUp } from '../../../utils/breakpoint'

const AutoGrowWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  position: relative;
  border: none;
  background: transparent;

  ${InputTextBase};
  line-height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});

  &::after {
    /* Note the weird space! Needed to prevent jumpy behavior */
    content: attr(data-replicated-value) ' ';
    /* This is how textarea text behaves */
    white-space: pre-wrap;
    /* Hidden from view, clicks, and screen readers */
    visibility: hidden;
    padding: ${rem(12)} ${rem(48)} ${rem(12)} ${rem(12)};
    border: 1px solid transparent;
    overflow-wrap: anywhere;
    grid-area: 1 / 1;
  }

  /* Place on top of each other */
  & > textarea {
    grid-area: 1 / 1;
    border: none;
    overflow: hidden;
    overflow-wrap: anywhere;

    &:not(:focus) {
      ${mediaQueryUp.tablet} {
        opacity: 0; 
        color: transparent;
        text-shadow: none;
      }
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`

const StyledTextArea = styled.textarea`
  ${InputTextBase};
  line-height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  padding: ${rem(14)};
  min-height: ${rem(48)};
  overflow: hidden;

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    color: ${props => props.theme.palette.neutral.default.four};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${props => props.theme.palette.neutral.default.two};
    }
  }

  /*
    Browser things like auto fill and validation pop ups use the
    actual textarea element as their target. We want this to look like
    the whole element - so we need our textarea to take up the whole container

    When there's icon, reserve space for them and then they will be
    absolutely positioned.

    Icons for inputs are always 24px sized
  */
  &.icon-left {
    padding-left: ${rem(48)};
  }

  &.icon-right {
    padding-right: ${rem(48)};
  }

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`

interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  inputClassName?: string
  containerRef?: React.Ref<HTMLDivElement>
  /**
   * Override the 'required' error message
   */
  requiredErrorMessage?: string
  /**
   * Override any other validation error
   *
   * _For more granular error handling, use getInvalidMessage_
   */
  invalidErrorMessage?: string
  manualError?: boolean
  noValidationSpacing?: boolean
  noValidationMessage?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  helpText?: React.ReactNode
  getInvalidMessage?: (textarea: HTMLTextAreaElement) => string | undefined
  onInputValidate?: (textarea: HTMLTextAreaElement) => void
  resize?: 'none' | 'both' | 'horizontal' | 'vertical'
}

const TextAreaOnly = forwardRef<HTMLTextAreaElement | null, Props>((props, ref) => {
  const {
    value,
    defaultValue,
    className,
    children,
    inputClassName,
    disabled,
    style,
    startIcon,
    endIcon,
    minLength,
    maxLength,
    noValidationSpacing,
    noValidationMessage,
    onInputValidate,
    manualError,
    containerRef,
    getInvalidMessage,
    requiredErrorMessage,
    invalidErrorMessage,
    helpText,
    ...inputProps
  } = props

  const [isDirty, setDirty] = useState<boolean>(!!(defaultValue || value))
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const handleDirtyChange = useCallback(() => {
    setDirty((currDirty) => {
      if (!currDirty) {
        return true
      } else {
        return currDirty
      }
    })
  }, [])

  const updateErrorMessage = useCallback((textarea: HTMLTextAreaElement) => {
    const validity = onInputValidate ? onInputValidate(textarea) : textarea.validity
    if (validity && !validity.valid) {
      let message = getInvalidMessage?.(textarea)
      if (!message) {
        if (validity.valueMissing && requiredErrorMessage) {
          message = requiredErrorMessage
        } else if (invalidErrorMessage) {
          message = invalidErrorMessage
        } else if (validity.tooShort) {
          message = `Please enter at least ${minLength} characters`
        } else if (validity.tooLong) {
          message = `Please enter at most ${maxLength} characters`
        } else {
          message = textarea.validationMessage
        }
      }
      setErrorMessage(message)
    } else if (errorMessage) {
      setErrorMessage(undefined)
    }
  }, [errorMessage, getInvalidMessage, invalidErrorMessage, maxLength, minLength, onInputValidate, requiredErrorMessage])

  const { onChange: propsOnChange, onInvalid: propsOnInvalid, onBlur: propsOnBlur, ...restInputProps } = inputProps

  const [currentValue, setCurrentValue] = useState(value || defaultValue || '')

  useEffect(() => {
    setCurrentValue(value || defaultValue || '')
  }, [value, defaultValue])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentValue(e.currentTarget.value)
    updateErrorMessage(e.currentTarget)
    propsOnChange?.(e)
  }, [updateErrorMessage, propsOnChange])

  const handleInvalid = useCallback((e: React.InvalidEvent<HTMLTextAreaElement>) => {
    updateErrorMessage(e.currentTarget)
    propsOnInvalid?.(e)
  }, [updateErrorMessage, propsOnInvalid])

  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    handleDirtyChange()
    updateErrorMessage(e.currentTarget)
    propsOnBlur?.(e)
  }, [handleDirtyChange, updateErrorMessage, propsOnBlur])

  let displayableErrorMessage = isDirty ? errorMessage : undefined

  if (manualError) {
    displayableErrorMessage = invalidErrorMessage
  }

  return <InputWrap
    ref={containerRef}
    className={className}
    error={displayableErrorMessage}
    disabled={disabled}
    noValidationSpacing={noValidationSpacing}
    noValidationMessage={noValidationMessage}
    startIcon={startIcon}
    endIcon={endIcon}
    helpText={helpText}
  >
    <AutoGrowWrapper data-replicated-value={currentValue}>
      <StyledTextArea
        {...restInputProps}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        minLength={minLength}
        maxLength={maxLength}
        disabled={disabled}
        className={cn(
          inputClassName,
          { 'icon-left': !!startIcon, 'icon-right': !!endIcon },
        )}
        onChange={handleChange}
        onInvalid={handleInvalid}
        onBlur={handleBlur}
        rows={1}
      />
    </AutoGrowWrapper>
    {children}
  </InputWrap>
})

export default TextAreaOnly
